/**
 * Analytics Configuration - PlugMonkey
 * Centralized configuration for all tracking services.
 * Single source of truth for tracker IDs, API keys, and URLs.
 *
 * Pattern: each public key reads from a NEXT_PUBLIC_* env var with the current
 * value as hardcoded fallback. Lets you override per environment (staging,
 * preview) without code changes, while keeping the project working with zero
 * env config.
 *
 * NEXT_PUBLIC_ prefix is required because this project is statically exported
 * (`output: 'export'`) — only NEXT_PUBLIC_* vars get inlined into the browser
 * bundle at build time. All keys below are PUBLIC by vendor design — safe to
 * commit fallbacks.
 */

import { BUNDLE_CHECKOUT_URL } from "@/lib/pricing-constants";

// ============================================================================
// Google Analytics & Ads Configuration
// ============================================================================

export const GOOGLE_ANALYTICS = {
  /**
   * Google Analytics 4 Measurement ID
   * Format: G-XXXXXXXXXX
   * Override via NEXT_PUBLIC_GA4_ID at build time.
   */
  GA4_ID: process.env.NEXT_PUBLIC_GA4_ID ?? "G-25C0Q8YJD6",

  /**
   * Google Ads Conversion Tracking ID
   * Format: AW-XXXXXXXXXX
   * Override via NEXT_PUBLIC_GOOGLE_ADS_ID at build time.
   */
  GOOGLE_ADS_ID: process.env.NEXT_PUBLIC_GOOGLE_ADS_ID ?? "AW-989961493",
} as const;

// ============================================================================
// Google Tag Manager Configuration
// ============================================================================

export const GOOGLE_TAG_MANAGER = {
  /**
   * Google Tag Manager Container ID
   * Format: GTM-XXXXXXX
   * Override via NEXT_PUBLIC_GTM_CONTAINER_ID at build time.
   */
  CONTAINER_ID: process.env.NEXT_PUBLIC_GTM_CONTAINER_ID ?? "GTM-WBS2QD6G",

  /**
   * Google tag gateway (first-party) measurement path, served by Cloudflare at the edge
   * (`/0pjy/healthy` returns "ok"; nginx never sees these requests). Cloudflare's automated
   * setup injects `<script src="/0pjy/">` into normal pages; GTMTracker then only pushes
   * gtm.js. On pages without it (404s) GTMTracker loads `/0pjy/?id=…` itself, so hits stay
   * first-party and ad blockers that block googletagmanager.com don't stop measurement. Google: with Consent Mode, automated script
   * setup must stay off and "you need to replace every instance of the existing Google tag
   * script source with your new measurement path" (support.google.com/google-ads/answer/16061406).
   * The gateway does not serve ns.html, so the <noscript> iframe stays on googletagmanager.com.
   * If this path ever changes in Google tag / Cloudflare, change it here.
   */
  GATEWAY_PATH: "/0pjy/",

  /**
   * Region-specific consent defaults
   * Regions where consent is granted by default (non-EU)
   */
  CONSENT_GRANTED_REGIONS: [
    "US",
    "CA",
    "AU",
    "JP",
    "KR",
    "SG",
    "HK",
    "TW",
    "NZ",
    "MY",
    "TH",
    "PH",
    "VN",
    "ID",
    "IN",
    "AE",
    "SA",
    "EG",
    "ZA",
    "BR",
    "AR",
    "MX",
    "CL",
    "CO",
    "PE",
    "VE",
    "UY",
    "PY",
    "BO",
    "EC",
    "GT",
    "HN",
    "CR",
    "PA",
    "NI",
    "SV",
    "CU",
    "DO",
    "JM",
    "TT",
    "BB",
    "GD",
    "LC",
    "VC",
    "KN",
    "AG",
    "DM",
    "BS",
    "BZ",
    "GY",
    "SR",
    "GF",
    "FK",
  ],
} as const;

// ============================================================================
// X (Twitter) Pixel Configuration
// ============================================================================

export const X_PIXEL = {
  /**
   * X (Twitter) Pixel ID.
   * Override via NEXT_PUBLIC_X_PIXEL_ID at build time.
   * (The EVENTS map below is derived per-conversion from the pixel; not
   * env-wrapped — rotate via X dashboard if needed.)
   */
  PIXEL_ID: process.env.NEXT_PUBLIC_X_PIXEL_ID ?? "pd5i8",

  /**
   * X Pixel Event IDs for conversion tracking
   */
  EVENTS: {
    /**
     * Lead generation event
     * Event ID: tw-pd5i8-pd5ia
     */
    LEAD: "tw-pd5i8-pd5ia",

    /**
     * Download event
     * Event ID: tw-pd5i8-pd5ib
     */
    DOWNLOAD: "tw-pd5i8-pd5ib",

    /**
     * Add to cart event
     * Event ID: tw-pd5i8-pd5ic
     */
    ADD_TO_CART: "tw-pd5i8-pd5ic",

    /**
     * Purchase event
     * Event ID: tw-pd5i8-pd5i9
     */
    PURCHASE: "tw-pd5i8-pd5i9",
  },
} as const;

// ============================================================================
// Meta (Facebook) Pixel Configuration
// ============================================================================

export const META_PIXEL = {
  /**
   * Meta Pixel ID. Public by design — fine to ship in the client bundle.
   * Override via NEXT_PUBLIC_META_PIXEL_ID at build time.
   */
  PIXEL_ID: process.env.NEXT_PUBLIC_META_PIXEL_ID ?? "36007767398837081",

  /**
   * Meta standard event names. Unlike X (which uses opaque per-conversion
   * event IDs), Meta uses literal string names for its standard event set.
   * See https://developers.facebook.com/docs/meta-pixel/reference#standard-events
   */
  EVENTS: {
    PAGE_VIEW: "PageView",
    VIEW_CONTENT: "ViewContent",
    LEAD: "Lead",
    INITIATE_CHECKOUT: "InitiateCheckout",
    PURCHASE: "Purchase",
    SUBSCRIBE: "Subscribe",
  },
} as const;

// ============================================================================
// PostHog Configuration
// ============================================================================

export const POSTHOG = {
  /**
   * PostHog Project API Key. Read from NEXT_PUBLIC_POSTHOG_KEY at build time
   * (statically inlined by Next.js because of the NEXT_PUBLIC_ prefix). Falls
   * back to the production project key so dev builds and forks without env
   * config keep working — PostHog project keys are public-by-design and not
   * a secret.
   */
  API_KEY: process.env.NEXT_PUBLIC_POSTHOG_KEY ?? "phc_wKoxcp8u7R3GOOhMDk5uFVRquHRoE0jih0i8F6cizIj",

  /**
   * PostHog API Host. Defaults to the t.plugmonkey.xyz reverse proxy that
   * bypasses ad blockers; can be overridden via NEXT_PUBLIC_POSTHOG_HOST for
   * staging/preview environments that should ingest into a separate project.
   */
  API_HOST: process.env.NEXT_PUBLIC_POSTHOG_HOST ?? "https://t.plugmonkey.xyz",

  /**
   * PostHog SDK Configuration Options
   *
   * NOT READ BY ANYTHING. Verified 2026-09-22: nothing imports or spreads this
   * object. The live options are hardcoded inside the inline init snippet in
   * components/PostHogTracker.tsx, because that snippet is serialised into a
   * <Script> tag as a template string and so cannot reference a runtime value.
   *
   * Editing values here changes NOTHING at runtime — change PostHogTracker.tsx
   * instead. This block is flagged rather than deleted because a config object
   * that looks authoritative and is silently inert is the more expensive
   * failure: someone turns a setting off here, sees the shape they expect in
   * the diff, and ships nothing. capture_performance: false lives in the
   * snippet and deliberately has no counterpart here.
   */
  CONFIG: {
    autocapture: true,
    capture_pageview: true,
    capture_pageleave: true,
  },
} as const;

// ============================================================================
// Lemon Squeezy Configuration
// ============================================================================

export const LEMON_SQUEEZY = {
  /** Bundle checkout URL — defined once in lib/pricing-constants.ts (no env override). */
  BUNDLE_CHECKOUT_URL,
} as const;

// ============================================================================
// Form Services Configuration
// ============================================================================

export const FORM_SERVICES = {
  /**
   * Web3Forms Access Key
   * Used for contact form + ModernFunnel submissions.
   * Override via NEXT_PUBLIC_WEB3FORMS_ACCESS_KEY at build time.
   * PUBLIC by Web3Forms's design — safe to commit fallback.
   */
  WEB3FORMS_ACCESS_KEY:
    process.env.NEXT_PUBLIC_WEB3FORMS_ACCESS_KEY ?? "ea506150-1c4e-4746-83b0-462b3028ed76",

  /**
   * ConvertKit API Key
   * Used for email marketing and newsletter subscriptions.
   * Override via NEXT_PUBLIC_CONVERTKIT_API_KEY at build time.
   * (ConvertKit's public form key is fine to ship client-side.)
   */
  CONVERTKIT_API_KEY:
    process.env.NEXT_PUBLIC_CONVERTKIT_API_KEY ?? "kit_d7881205ea7c974a794e53be03a3d8cd",
} as const;

// ============================================================================
// Consent Management Configuration
// ============================================================================

export const CONSENT = {
  /**
   * LocalStorage key for storing consent preferences
   */
  STORAGE_KEY: "cookie-consent",

  /**
   * Consent expiration in days
   */
  EXPIRATION_DAYS: 365,

  /**
   * GA4's default for opt-out-country visitors who haven't chosen (measurement-consent C11).
   * false = full (cookies, gcs=G111) — required while the property is under Google's modeling
   * threshold of 1,000 daily users with analytics_storage granted (7 of the previous 28 days),
   * because cookieless pings are reported only through that modeling. true = cookieless pings
   * (G100) once past it. plugmonkey.xyz, 2026-11-04: ~50–70 GA4 sessions a day → false.
   * PostHog runs full for every allowed visitor regardless; session replay needs an explicit Accept.
   */
  GA4_COOKIELESS_DEFAULT: false,

  /**
   * Default consent state (OPT-OUT model)
   * Consent granted by default for all regions except GDPR
   * US and most countries allow opt-out with default opt-in
   */
  DEFAULTS: {
    ad_storage: "granted",
    ad_user_data: "granted",
    ad_personalization: "granted",
    analytics_storage: "granted",
    functionality_storage: "granted",
    personalization_storage: "granted",
    security_storage: "granted",
  },

  /**
   * EU/GDPR regions that require OPT-IN (denied by default)
   * These override the global DEFAULTS to 'denied'
   */
  EU_REGIONS: [
    "AT",
    "BE",
    "BG",
    "HR",
    "CY",
    "CZ",
    "DK",
    "EE",
    "FI",
    "FR",
    "DE",
    "GR",
    "HU",
    "IE",
    "IT",
    "LV",
    "LT",
    "LU",
    "MT",
    "NL",
    "PL",
    "PT",
    "RO",
    "SK",
    "SI",
    "ES",
    "SE",
    "GB",
    "IS",
    "LI",
    "NO",
    "CH",
  ],

  // No WAIT_FOR_UPDATE: wait_for_update stopped every GA4 hit until a consent update
  // (GTMTracker.tsx has the measurement). Stored choices are applied before GTM loads.
} as const;

// ============================================================================
// Analytics Event Names
// ============================================================================

/**
 * Standard event names used across all tracking platforms
 * DO NOT CHANGE - These are used for tracking active ad campaigns
 */
export const EVENT_NAMES = {
  // Ecommerce Events
  VIEW_PRODUCT: "view_product",
  ADD_TO_CART: "add_to_cart",
  BEGIN_CHECKOUT: "begin_checkout",
  PURCHASE: "purchase",

  // Lead Generation Events
  //
  // generate_lead is reserved for genuine sales leads — someone asking us for
  // something, not someone asking to hear from us. GA4 defines its parameters
  // as currency/value/lead_source, so every lead carries a value.
  GENERATE_LEAD: "generate_lead",
  QUALIFY_LEAD: "qualify_lead",

  // Newsletter / audience growth.
  //
  // NOT a lead and NOT a GA4 recommended event. GA4 has no `subscribe`; the
  // closest native event is `sign_up`, which officially means "signed up for an
  // account" — wrong semantics for an email newsletter. A custom name keeps
  // this audience out of lead metrics instead of blurring the two.
  NEWSLETTER_SIGNUP: "newsletter_signup",

  // Form interaction events (GA4 enhanced-measurement event names).
  //
  // We emit these manually rather than relying on enhanced measurement: our
  // forms submit via fetch(), so EM's native-submit detection cannot see them.
  // Enhanced measurement form interactions MUST stay disabled or these will
  // double-count.
  FORM_START: "form_start",
  FORM_SUBMIT: "form_submit",

  // Extension feedback outcomes. Customers disengaging, not prospects
  // engaging — deliberately separate from the lead funnel above.
  UNINSTALL_SUBMITTED: "uninstall_submitted",
  FEEDBACK_SUBMITTED: "feedback_submitted",

  // Consent Events
  CONSENT_UPDATE: "consent_update",

  // Thank-You Activation Flow Events
  THANK_YOU_KEY_COPIED: "thank_you_key_copied",
  THANK_YOU_KEY_DOWNLOADED: "thank_you_key_downloaded",
  THANK_YOU_PRINT_CLICKED: "thank_you_print_clicked",

  // Uninstall Save-the-Customer Offer Events
  UNINSTALL_OFFER_SHOWN: "uninstall_offer_shown",
  UNINSTALL_OFFER_COUPON_COPIED: "uninstall_offer_coupon_copied",
  UNINSTALL_OFFER_CTA_CLICKED: "uninstall_offer_cta_clicked",
  UNINSTALL_OFFER_DISMISSED: "uninstall_offer_dismissed",

  // Install Funnel Events (close the funnel blind spot between view_product
  // and the /install/ pageview: the X pixel fires Download on install clicks
  // but PostHog previously saw nothing for that step)
  INSTALL_BUTTON_CLICKED: "install_button_clicked",
  INSTALL_UPSELL_STRIP_CLICKED: "install_upsell_strip_clicked",
} as const;

// ============================================================================
// Default Ecommerce Values
// ============================================================================

export const ECOMMERCE_DEFAULTS = {
  /**
   * Default currency for all transactions
   */
  CURRENCY: "USD",

  /**
   * Default product category
   */
  DEFAULT_CATEGORY: "Chrome Extension",

  /**
   * Default product value (fallback when actual price unknown)
   * Must match PRICING.MONTHLY in lib/pricing-constants.ts
   */
  DEFAULT_VALUE: "6.99",
} as const;
